import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabNav } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DialogDeleteComponent } from 'src/app/common/dialogs/dialog-delete/dialog-delete.component';
import { ErrorMessage } from 'src/app/common/messages';
import { isAllowedToChangeDashboardAccess, isDashboardEditable } from 'src/app/dashboards/dashboard-helpers/helpers';
import { getActiveDashboard, getActiveTabWidgets, getDashboardsConfigState, getDashboardsState, getIsDirtyState, getselectedDashboard, State } from 'src/app/dashboards/state';
import { getUserInfoState } from 'src/app/dashboards/state/index';
import { AppEventType } from 'src/app/enums';
import { dashboard, gridItem } from 'src/app/models';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import { AppEvent, EventQueueService } from 'src/app/services/services/EventQueueService';
import * as mainActions from 'src/app/state/app.actions';
import { DashboardFilterComponent } from '../components/dashboard-filter/dashboard-filter.component';
import { DialogDashboardNew } from '../components/dialog-dashboard-new/dialog-dashboard-new';
import * as actions from './../state/actions';

@Component({
    selector: 'app-dashboard-nav',
    templateUrl: './dashboard-nav.component.html',
    styleUrls: ['./dashboard-nav.component.scss'],
    standalone: false
})

export class DashboardNavComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  dashboards$: Observable<dashboard[]>;
  filteredDashboards$: Observable<dashboard[]>;
  isDirtyState$: Observable<boolean>;
  selectedDashboard$: Observable<number>;
  getActiveDashboard$: Observable<dashboard>;
  activeTabWidgets$: Observable<gridItem[]>;
  userInfo$: Observable<any>;
  editDashId: number = null;
  isDirty: boolean = false;
  maxWidgets: number;
  maxDashboards: number;
  activeDashboard: dashboard;
  editName: string;
  allowToEditOtherUsersDashboards: boolean; // property per user
  isRoot: boolean;
  isPartner: boolean;
  ownerName: string;
  isActiveDashboardEditable: boolean;
  @ViewChild('matTab') private matTab: MatTabNav
  isReadOnly: boolean = false;
  owners: any[] = [];

  constructor(private dashboardService: DashboardService, private store: Store<State>, public dialog: MatDialog, private i18nextPipe: I18NextPipe, private logger: NGXLogger,
    private eventQueue: EventQueueService) {
    this.dashboards$ = this.store.select(getDashboardsState);
    this.selectedDashboard$ = this.store.select(getselectedDashboard);
    this.activeTabWidgets$ = this.store.select(getActiveTabWidgets);

    this.isDirtyState$ = this.store.select(getIsDirtyState);
    this.isDirtyState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(isDirtyState => { this.isDirty = isDirtyState });

    this.getActiveDashboard$ = this.store.select(getActiveDashboard);
    this.getActiveDashboard$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(d => {
      this.activeDashboard = ({ ...d });
      this.refreshTabs();

      d && (this.isActiveDashboardEditable = isDashboardEditable(d, this.isRoot, this.isPartner, this.allowToEditOtherUsersDashboards, this.isReadOnly));
    });

    this.store.select(getDashboardsConfigState).pipe(take(1)).subscribe(c => {
      this.maxWidgets = c.maxWidgetsPerDashboard;
      this.maxDashboards = c.maxDashboards;
    });

    this.store.select(getDashboardsState).pipe(takeUntil(this.ngUnsubscribe)).subscribe(d => { this.refreshTabs(); });

    this.userInfo$ = this.store.select(getUserInfoState);
    this.userInfo$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(info => {
      this.isRoot = info?.userProperties?.isRoot;
      this.isPartner = info?.userProperties?.isPartner;
      this.allowToEditOtherUsersDashboards = info?.userProperties?.allowToEditOtherUsersDashboards;
      this.ownerName = info?.ownerName;
      this.isReadOnly = info?.userProperties?.isDashboardsReadOnly;
    });
  }

  refreshTabs() {
    setTimeout(() => {
      if (this.matTab) {
        this.matTab._scrollToLabel(this.matTab.selectedIndex);
        this.matTab._updateTabScrollPosition();
        this.matTab._alignInkBarToSelectedTab();
      }
    }, 0);
    setTimeout(() => {
      if (this.matTab) {
        this.matTab._scrollToLabel(this.matTab.selectedIndex);
        this.matTab._updateTabScrollPosition();
        this.matTab._alignInkBarToSelectedTab();
      }
    }, 300);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit(): void {
    this.dashboardService.getAllAgents().subscribe(res => {
      this.owners = res;
    })
  }

  dashboardAdd($e): void {
    this.logger.trace('DashboardNavComponent:dashboardAdd event');
    this.dashboards$.pipe(take(1)).subscribe((d) => {
      if (d.filter(this.userOwned).length < this.maxDashboards) {
        const dialogRef = this.dialog.open(DialogDashboardNew, {
          panelClass: 'dialog-standard-pannel',
          backdropClass: "backdrop-invisible",
          disableClose: true,
          data: {},
          width: "440px",
          height: "210px"
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.logger.trace('DashboardNavComponent:dashboardAdd', result);
            let newDash: dashboard = {
              name: result.name,
              isPublic: result.isPublic,
              userOwned: true,
              visible: true,
              isFavorite: false,
              orderNo: 99999,
              ownerName: this.ownerName,
              allowNonOwnersToEdit: false,
            };
            this.store.dispatch(actions.addDashboard({ dashboard: newDash }));
          }
        });
      }
    })
  };

  dashboardsSave() {
    this.isDirtyState$.pipe(take(1)).subscribe(isDirtyState => {
      if (isDirtyState) {
        this.logger.trace('DashboardNavComponent:dashboardsSave');
        this.store.dispatch(actions.saveDashboards());
      }
    });
  }

  dashboardsCancel() {
    if (this.isDirty) {
      this.logger.trace('DashboardNavComponent:dashboardsCancel');
      this.store.dispatch(actions.ClearCurrentDashboard());
      this.store.dispatch(actions.loadDashboards());

      this.eventQueue.dispatch(new AppEvent(AppEventType.DashboardsCancelEvent, true));
    }
  }

  dashboardRemoveActive() {
    let dash: dashboard;
    this.selectedDashboard$.pipe(take(1)).subscribe(selectedId => {
      this.dashboards$.pipe(take(1)).subscribe(d => {
        dash = d.find(f => f.id == selectedId && d.length > 1 && d.filter(this.dashboardVisible).length > 1);
      });
    });
    if (!dash) return;
    this.logger.trace('DashboardNavComponent:dashboardRemoveActive');

    this.dashboardService.getDashboardUsage(dash.id).subscribe(usage => {
      let title = dash.isPublic ? "Are you sure you want to delete this Company Dashboard?" : "Are you sure you want to delete this Private Dashboard?";
      let additionalText: string = null;
      let additionalSecondText: string = this.i18nextPipe.transform('dashboard:All contained widgets will be deleted')
      if (dash.isPublic && usage == 0) {
        additionalText = "Currently no other users use it.";
      }
      if (dash.isPublic && usage > 0) {
        additionalText = "Currently at least one other users have selected it.";
      }

      const dialogRef = this.dialog.open(DialogDeleteComponent, {
        disableClose: true,
        data: {
          title: this.i18nextPipe.transform('dashboard:' + title),
          additionalText: additionalText ? this.i18nextPipe.transform('dashboard:' + additionalText) : null,
          additionalSecondText
        }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.logger.trace('DashboardNavComponent:dashboardRemoveActive', dash);
          this.store.dispatch(actions.removeDashboard({ dashboard: dash }));
        }
      });
    })
  }

  selectDashboard(dashboard: dashboard) {
    this.editName = dashboard.name;
    this.logger.trace('DashboardNavComponent:selectDashboard', dashboard);
    this.store.dispatch(actions.selectDashboard({ dashboard: dashboard }));
  }

  dashboardEdit($event, d: dashboard) {
    if (!isDashboardEditable(d, this.isRoot, this.isPartner, this.allowToEditOtherUsersDashboards, this.isReadOnly)) {
      this.store.dispatch(
        mainActions.addMessage({
          message: new ErrorMessage(
            this.i18nextPipe.transform('messages:Cannot Edit'),
            this.i18nextPipe.transform("messages:You do not have permissions to edit this shared Dashboard"))
        }));
      return;
    }
    this.editDashId = d.id;
    let parent = $event.currentTarget.parentElement as HTMLElement
    setTimeout(() => {
      (<HTMLElement>parent.firstChild).focus();
    }, 10)
  }

  dashboardSaveEdit($event, dashboard: dashboard) {
    this.updateDashboardName($event.target?.value, dashboard);
  }

  dashboardEditBlur($event, dashboard: dashboard) {
    this.updateDashboardName($event.target?.value, dashboard);
  }

  updateDashboardName(newName: string, dashboard: dashboard) {
    if (newName && dashboard.name != newName) {
      this.store.dispatch(actions.updateDashboardName({ dashboard, newName }));
    }
    this.editDashId = null;
  }

  dashboardAbordEdit($event) {
    this.editDashId = null;
  }

  updateDashboardAccess($event, isPublic) {
    // let isPublic = $event.value;

    // isPublic = $event.target.id == "access_public";
    this.store.dispatch(actions.updateDashboardAccess({ dashboard: this.activeDashboard, isPublic }));

    if (!isPublic && this.activeDashboard.allowNonOwnersToEdit) {
      this.store.dispatch(actions.updateDashboardAllowNonOwnersToEdit({ dashboard: this.activeDashboard, allowOthersToEdit: false }));
    }
  }

  dashboardsFilter(): void {
    this.logger.trace('DashboardNavComponent:dashboardsFilter event');
    this.dialog.open(DashboardFilterComponent, {
      panelClass: 'dialog-filter-pannel',
      disableClose: true,
      data: {
        allowToEditOtherUsersDashboards: this.allowToEditOtherUsersDashboards,
        isReadOnly: this.isReadOnly,
        owners: this.owners
      }
    });
  };

  userOwned(d: dashboard): boolean {
    return d.userOwned;
  }

  dashboardVisible(d: dashboard): boolean {
    return d.visible;
  }

  allowOtherToEditClick($event) {
    let delayToCloseDialog = 500,
      currentTarget = $event.currentTarget;

    $event.stopPropagation();

    setTimeout(() => { currentTarget.parentElement.dispatchEvent($event); }, delayToCloseDialog);
  }

  updateAllowNonOwnersToEdit($event) {
    let allowOthersToEdit = $event.checked;

    this.store.dispatch(actions.updateDashboardAllowNonOwnersToEdit({ dashboard: this.activeDashboard, allowOthersToEdit }));
  }

  isAllowedToChangeAccess(dashboard: dashboard): boolean {
    return isAllowedToChangeDashboardAccess(dashboard, this.isRoot, this.isPartner, this.allowToEditOtherUsersDashboards, this.isReadOnly);
  }
}
